import { useEffect, useState } from 'react';
import { addEmployee, updateEmployee, deleteEmployee, assignEmployeeToArea, removeEmployeeFromArea, getDeviationsAllTime, Employee, Area, EmployeeType } from '../api';
import { api } from '../api/client';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import '../components/common/Modal.css';
import Spinner from '../components/common/Spinner';
import './Page.css';

type SortField = 'employeeId' | 'name' | 'title' | 'deviations';

const TYPE_LABELS: Record<EmployeeType, string> = {
  regular: 'Regular',
  'ex-employee': 'Ex-Employee',
  'Other DCs': 'Other DCs',
};

const TYPE_BADGE_CLASS: Record<EmployeeType, string> = {
  regular: 'emp-type--regular',
  'ex-employee': 'emp-type--ex',
  'Other DCs': 'emp-type--visiting',
};
type SortDir = 'asc' | 'desc';

const sortIcon = (field: SortField, sortField: SortField, sortDir: SortDir) => {
  const active = sortField === field;
  return (
    <svg className="sort-svg" width="12" height="16" viewBox="0 0 12 16" fill="none">
      <path d="M6 2L2 7H10L6 2Z" fill={active && sortDir === 'asc' ? '#7B2D8B' : '#D5A8E8'} />
      <path d="M6 14L2 9H10L6 14Z" fill={active && sortDir === 'desc' ? '#7B2D8B' : '#D5A8E8'} />
    </svg>
  );
};

const Employees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [deviationCounts, setDeviationCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Employee | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [assignAreaId, setAssignAreaId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('employeeId');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const [typeFilter, setTypeFilter] = useState<EmployeeType | ''>('');
  const [form, setForm] = useState({ employeeId: '', name: '', areaId: '', canMakeInterview: false, employeeType: 'regular' as EmployeeType });
  const [editForm, setEditForm] = useState({ employeeId: '', name: '', title: '', canMakeInterview: false, employeeType: 'regular' as EmployeeType });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get<Employee[]>('/admin/employees').catch(() => []),
      api.get<Area[]>('/admin/areas').catch(() => []),
      getDeviationsAllTime().catch(() => ({ deviations: [] })),
    ])
      .then(([emps, ars, devData]) => {
        setEmployees(emps as Employee[]);
        setAreas(ars as Area[]);
        const counts: Record<string, number> = {};
        for (const r of (devData as { deviations: { employeeId?: { _id: string } }[] }).deviations ?? []) {
          const id = r.employeeId?._id;
          if (id) counts[id] = (counts[id] ?? 0) + 1;
        }
        setDeviationCounts(counts);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await addEmployee({ employeeId: Number(form.employeeId), name: form.name, areaId: form.areaId, canMakeInterview: form.canMakeInterview, employeeType: form.employeeType });
      setShowAddModal(false);
      setForm({ employeeId: '', name: '', areaId: '', canMakeInterview: false, employeeType: 'regular' });
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to add employee');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (emp: Employee) => {
    setSelectedEmployee(emp);
    setEditForm({
      employeeId: String(emp.employeeId),
      name: emp.name,
      title: emp.title,
      canMakeInterview: emp.canMakeInterview,
      employeeType: emp.employeeType ?? 'regular',
    });
    setFormError('');
    setShowEditModal(true);
  };

  const handleEdit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    setFormError('');
    setSubmitting(true);
    try {
      await updateEmployee(selectedEmployee._id, {
        employeeId: Number(editForm.employeeId),
        name: editForm.name,
        title: editForm.title,
        canMakeInterview: editForm.canMakeInterview,
        employeeType: editForm.employeeType,
      });
      setShowEditModal(false);
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to update employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteEmployee(confirmDelete._id);
      setConfirmDelete(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete employee');
    }
  };

  const handleAssign = async () => {
    if (!selectedEmployee || !assignAreaId) return;
    setSubmitting(true);
    try {
      await assignEmployeeToArea(selectedEmployee._id, assignAreaId);
      setAssignAreaId('');
      const updated = await api.get<{ areas: Area[] }>(`/admin/employee-areas/${selectedEmployee._id}`);
      setSelectedEmployee({ ...selectedEmployee, areas: updated.areas });
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveArea = async (areaId: string) => {
    if (!selectedEmployee) return;
    try {
      await removeEmployeeFromArea(selectedEmployee._id, areaId);
      const updated = await api.get<{ areas: Area[] }>(`/admin/employee-areas/${selectedEmployee._id}`);
      setSelectedEmployee({ ...selectedEmployee, areas: updated.areas });
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to remove');
    }
  };

  const filtered = employees
    .filter((e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) &&
      (typeFilter === '' || e.employeeType === typeFilter)
    )
    .sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortField === 'employeeId') { av = a.employeeId; bv = b.employeeId; }
      else if (sortField === 'name') { av = a.name; bv = b.name; }
      else if (sortField === 'title') { av = a.title ?? ''; bv = b.title ?? ''; }
      else if (sortField === 'deviations') { av = deviationCounts[a._id] ?? 0; bv = deviationCounts[b._id] ?? 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const employeeAreas = selectedEmployee
    ? ((selectedEmployee.areas ?? []) as Area[]).filter((a): a is Area => typeof a !== 'string')
    : [];

  const unassignedAreas = areas.filter(
    (a) => !employeeAreas.some((ea) => ea._id === a._id)
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">{employees.length} line staff members</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Employee
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="search-input"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="area-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as EmployeeType | '')}>
          <option value="">All Types</option>
          <option value="regular">Regular</option>
          <option value="ex-employee">Ex-Employee</option>
          <option value="Other DCs">Other DCs</option>
        </select>
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('employeeId')}>ID {sortIcon('employeeId', sortField, sortDir)}</th>
                <th className="th-sortable" onClick={() => handleSort('name')}>Name {sortIcon('name', sortField, sortDir)}</th>
                <th>Type</th>
                <th className="th-sortable" onClick={() => handleSort('title')}>Title {sortIcon('title', sortField, sortDir)}</th>
                <th>Can Interview</th>
                <th>Areas</th>
                <th className="th-sortable" onClick={() => handleSort('deviations')}>Deviations {sortIcon('deviations', sortField, sortDir)}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="empty-cell">No employees found</td></tr>
              ) : (
                filtered.map((emp) => (
                  <tr key={emp._id}>
                    <td>{emp.employeeId}</td>
                    <td><strong>{emp.name}</strong></td>
                    <td>
                      {emp.employeeType !== 'regular' && (
                        <span className={`emp-type-badge ${TYPE_BADGE_CLASS[emp.employeeType ?? 'regular']}`}>
                          {TYPE_LABELS[emp.employeeType ?? 'regular']}
                        </span>
                      )}
                    </td>
                    <td>{emp.title}</td>
                    <td>{emp.canMakeInterview ? '✓' : '—'}</td>
                    <td>
                      <div className="areas-pill-list">
                        {((emp.areas ?? []) as Area[]).map((a) =>
                          typeof a === 'string' ? null : (
                            <span key={a._id} className={`area-tag${a._id === (emp.mainArea as unknown as Area)?._id ? ' area-tag--main' : ''}`}>
                              {a.name}{a._id === (emp.mainArea as unknown as Area)?._id ? ' ★' : ''}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="index-badge">{deviationCounts[emp._id] ?? 0}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                        <button
                          className="btn-sm"
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setFormError('');
                            setShowAreaModal(true);
                          }}
                        >
                          Manage Areas
                        </button>
                        <button className="btn-sm btn-danger" onClick={() => setConfirmDelete(emp)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <Modal title="Add Employee" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAdd}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-row">
              <div className="form-field">
                <label>Employee ID</label>
                <input type="number" min="1" max="99999999999" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} placeholder="Up to 11 digits" required />
              </div>
              <div className="form-field">
                <label>Full Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Employee name" required />
              </div>
            </div>
            <div className="form-field">
              <label>Employee Type</label>
              <select value={form.employeeType} onChange={(e) => setForm({ ...form, employeeType: e.target.value as EmployeeType })}>
                <option value="regular">Regular</option>
                <option value="ex-employee">Ex-Employee</option>
                <option value="Other DCs">Other DCs</option>
              </select>
            </div>
            <div className="form-field">
              <label>Area (sets Title &amp; Main Area)</label>
              <select value={form.areaId} onChange={(e) => setForm({ ...form, areaId: e.target.value })} required>
                <option value="">Select area</option>
                {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>
                <input type="checkbox" checked={form.canMakeInterview} onChange={(e) => setForm({ ...form, canMakeInterview: e.target.checked })} style={{ marginRight: 8 }} />
                Can conduct interviews
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add Employee'}</button>
            </div>
          </form>
        </Modal>
      )}

      {showEditModal && selectedEmployee && (
        <Modal title={`Edit — ${selectedEmployee.name}`} onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEdit}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-row">
              <div className="form-field">
                <label>Employee ID</label>
                <input type="number" min="1" max="99999999999" value={editForm.employeeId} onChange={(e) => setEditForm({ ...editForm, employeeId: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Full Name</label>
                <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              </div>
            </div>
            <div className="form-field">
              <label>Title</label>
              <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required />
            </div>
            <div className="form-field">
              <label>Employee Type</label>
              <select value={editForm.employeeType} onChange={(e) => setEditForm({ ...editForm, employeeType: e.target.value as EmployeeType })}>
                <option value="regular">Regular</option>
                <option value="ex-employee">Ex-Employee</option>
                <option value="Other DCs">Other DCs</option>
              </select>
            </div>
            <div className="form-field">
              <label>
                <input type="checkbox" checked={editForm.canMakeInterview} onChange={(e) => setEditForm({ ...editForm, canMakeInterview: e.target.checked })} style={{ marginRight: 8 }} />
                Can conduct interviews
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete Employee"
          message={`Are you sure you want to delete ${confirmDelete.name}? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {showAreaModal && selectedEmployee && (
        <Modal title={`Manage Areas — ${selectedEmployee.name}`} onClose={() => setShowAreaModal(false)}>
          {formError && <p className="form-error">{formError}</p>}
          <div className="form-field">
            <label>Current Areas</label>
            <div className="areas-pill-list" style={{ minHeight: 32 }}>
              {employeeAreas.length === 0 && <span style={{ color: '#7A5C8A', fontSize: 13 }}>No areas assigned</span>}
              {employeeAreas.map((a) => (
                <span key={a._id} className="areas-pill">
                  {a.name}
                  <button className="pill-remove" onClick={() => handleRemoveArea(a._id)}>✕</button>
                </span>
              ))}
            </div>
          </div>
          <div className="form-field">
            <label>Assign to Area</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={assignAreaId} onChange={(e) => setAssignAreaId(e.target.value)} style={{ flex: 1 }}>
                <option value="">Select area</option>
                {unassignedAreas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              </select>
              <button
                type="button"
                className="btn-primary"
                style={{ padding: '10px 16px' }}
                disabled={!assignAreaId || submitting}
                onClick={handleAssign}
              >
                Assign
              </button>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setShowAreaModal(false)}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Employees;
